<script setup lang="ts">
import {
  computed,
  nextTick,
  onUnmounted,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import { isJust, isNothing, just, nothing, type Maybe } from "../../lib/maybe";

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    /** Callers pass `just(text)` or `nothing` explicitly. */
    description: Maybe<string>;
    /**
     * Panel width scale.
     * `palette` is 34rem (project search / command-palette width).
     */
    size?: "sm" | "md" | "palette" | "lg" | "xl";
    /**
     * cozy: padded panel for simple forms.
     * sheet: flush panel with bordered header/footer for larger layouts.
     */
    layout?: "cozy" | "sheet";
    /**
     * center: modal in the middle of the viewport (default).
     * top: command-palette style, near the top.
     */
    placement?: "center" | "top";
    /**
     * Keep the title for aria-labelledby but hide it visually (palette UIs
     * that lead with a search field).
     */
    titleHidden?: boolean;
    showClose?: boolean;
    closeOnBackdrop?: boolean;
    zIndex?: number;
  }>(),
  {
    size: "sm",
    layout: "cozy",
    placement: "center",
    titleHidden: false,
    showClose: false,
    closeOnBackdrop: true,
    zIndex: 100,
  },
);

const emit = defineEmits<{
  close: [];
}>();

const titleId = useId();
const panel = useTemplateRef<HTMLElement>("panel");

const backdropStyle = computed(() => ({
  zIndex: props.zIndex,
}));

/** Guard: Vue can still omit a required Maybe prop at the edge. */
const descriptionM = computed((): Maybe<string> => {
  const description = props.description;
  return typeof description === "undefined" ? nothing : description;
});

const showDescription = computed(
  () => isJust(descriptionM.value) && props.titleHidden === false,
);

const descriptionText = computed(() =>
  isJust(descriptionM.value) ? descriptionM.value.value : "",
);

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

let returnFocus: Maybe<HTMLElement> = nothing;

let escapeListener: Maybe<(event: KeyboardEvent) => void> = nothing;

function focusableInDialog(): HTMLElement[] {
  const root = panel.value;
  if (root === null) return [];
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    (el) =>
      el.hasAttribute("disabled") === false &&
      el.getAttribute("aria-hidden") !== "true" &&
      el.tabIndex !== -1,
  );
}

function onBackdropPointerDown(): void {
  if (props.closeOnBackdrop) emit("close");
}

function menuIsOpen(): boolean {
  return document.querySelector('.zw-menu [aria-expanded="true"]') !== null;
}

function onEscape(event: KeyboardEvent): void {
  if (event.key !== "Escape" || event.defaultPrevented) return;
  if (menuIsOpen()) return;
  event.preventDefault();
  emit("close");
}

function bindEscape(): void {
  unbindEscape();
  escapeListener = just(onEscape);
  document.addEventListener("keydown", escapeListener.value, true);
}

function unbindEscape(): void {
  if (isNothing(escapeListener)) return;
  document.removeEventListener("keydown", escapeListener.value, true);
  escapeListener = nothing;
}

function focusDialog(): void {
  const root = panel.value;
  if (root === null) return;
  if (root.contains(document.activeElement)) return;
  const first = focusableInDialog()[0];
  (first ?? root).focus();
}

watch(
  () => props.open,
  async (open) => {
    if (open === false) {
      unbindEscape();
      await nextTick();
      if (isJust(returnFocus) && returnFocus.value.isConnected === true) {
        returnFocus.value.focus();
      }
      returnFocus = nothing;
      return;
    }
    returnFocus =
      document.activeElement instanceof HTMLElement
        ? just(document.activeElement)
        : nothing;
    bindEscape();
    await nextTick();
    focusDialog();
  },
  { immediate: true },
);

onUnmounted(unbindEscape);

function onDialogKeydown(event: KeyboardEvent): void {
  if (event.key !== "Tab") return;

  const focusable = focusableInDialog();
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const activeM =
    document.activeElement instanceof HTMLElement
      ? just(document.activeElement)
      : nothing;
  const panelEl = panel.value;
  const activeInPanel =
    panelEl !== null &&
    isJust(activeM) &&
    panelEl.contains(activeM.value) === true;
  if (event.shiftKey) {
    if (
      isNothing(activeM) ||
      activeM.value === first ||
      activeInPanel === false
    ) {
      event.preventDefault();
      last.focus();
    }
  } else if (
    isNothing(activeM) ||
    activeM.value === last ||
    activeInPanel === false
  ) {
    event.preventDefault();
    first.focus();
  }
}

defineExpose({
  el: panel,
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="zw-dialog-backdrop"
      :data-placement="placement"
      role="presentation"
      :style="backdropStyle"
      @pointerdown.self="onBackdropPointerDown"
    >
      <div
        ref="panel"
        class="zw-dialog-panel"
        :data-size="size"
        :data-layout="layout"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @pointerdown.stop
        @keydown="onDialogKeydown"
      >
        <header
          class="zw-dialog-header"
          :class="{
            'zw-dialog-header--sr-only':
              titleHidden === true && showClose === false,
          }"
        >
          <div class="zw-dialog-heading">
            <h2
              :id="titleId"
              class="zw-dialog-title"
              :class="{ 'zw-dialog-title--sr-only': titleHidden }"
            >
              {{ title }}
            </h2>
            <p v-if="showDescription" class="zw-dialog-description">
              {{ descriptionText }}
            </p>
          </div>
          <button
            v-if="showClose"
            type="button"
            class="zw-dialog-close"
            title="Close"
            aria-label="Close"
            @click="emit('close')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="size-4"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 6l12 12M18 6 6 18"
              />
            </svg>
          </button>
        </header>

        <div class="zw-dialog-body">
          <slot />
        </div>

        <footer
          v-if="typeof $slots.footer === 'function'"
          class="zw-dialog-footer"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style>
.zw-dialog-backdrop {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background: var(--backdrop);
  backdrop-filter: blur(2px);
}

.zw-dialog-backdrop[data-placement="top"] {
  place-items: start center;
  padding: min(18vh, 7rem) 1rem 1.5rem;
}

.zw-dialog-panel {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 0.5rem;
  background: var(--bg-raised);
  box-shadow: var(--shadow-modal);
  font-family: var(--font-ui);
  max-width: 100%;
  outline: none;
}

.zw-dialog-panel[data-size="sm"] {
  width: min(100%, 22rem);
}

.zw-dialog-panel[data-size="md"] {
  width: min(100%, 24rem);
}

.zw-dialog-panel[data-size="palette"] {
  width: min(100%, 34rem);
  max-height: min(70vh, 28rem);
}

.zw-dialog-panel[data-size="lg"] {
  width: min(100%, 52rem);
  max-height: min(92vh, 44rem);
}

.zw-dialog-panel[data-size="xl"] {
  width: min(100%, 56rem);
  height: min(92vh, 44rem);
}

.zw-dialog-panel[data-layout="cozy"] {
  gap: 0.85rem;
  padding: 1rem 1.1rem 0.9rem;
}

.zw-dialog-panel[data-layout="sheet"] {
  overflow: hidden;
}

.zw-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.zw-dialog-header--sr-only,
.zw-dialog-title--sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.zw-dialog-panel[data-layout="sheet"] .zw-dialog-header {
  padding: 1.05rem 1.15rem 0.85rem;
  border-bottom: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
}

.zw-dialog-title {
  margin: 0;
  font-family: var(--font-heading);
  font-size: 1.12rem;
  font-weight: 400;
  color: var(--text-strong);
}

.zw-dialog-panel[data-size="lg"] .zw-dialog-title,
.zw-dialog-panel[data-size="xl"] .zw-dialog-title {
  font-size: 1.22rem;
}

.zw-dialog-description {
  margin: 0.3rem 0 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--text-muted);
}

.zw-dialog-panel[data-size="xl"] .zw-dialog-description {
  font-size: 0.95rem;
  max-width: 52ch;
}

.zw-dialog-close {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  padding: 0.35rem;
  border-radius: 0.3rem;
  cursor: pointer;
  flex-shrink: 0;
}

.zw-dialog-close:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.zw-dialog-body {
  min-width: 0;
}

.zw-dialog-panel[data-layout="cozy"] .zw-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  -webkit-user-select: text;
  user-select: text;
}

.zw-dialog-panel[data-size="palette"][data-layout="cozy"] {
  min-height: 0;
  overflow: hidden;
  /* Extra inset so ZwInput's focus outline (offset 1px) is not cropped. */
  padding: 1.15rem 1.25rem 1.05rem;
}

.zw-dialog-panel[data-size="palette"][data-layout="cozy"] .zw-dialog-body {
  flex: 1;
  min-height: 0;
  /* Clip the result list, not the search field outline. */
  overflow: hidden;
  gap: 0.55rem;
  padding: 0.15rem;
}

.zw-dialog-panel[data-layout="sheet"] .zw-dialog-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  -webkit-user-select: text;
  user-select: text;
}

.zw-dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.zw-dialog-panel[data-layout="cozy"] .zw-dialog-footer {
  padding-top: 0.15rem;
}

.zw-dialog-panel[data-layout="sheet"] .zw-dialog-footer {
  padding: 0.75rem 1.1rem 0.9rem;
  border-top: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
}

.zw-dialog-error {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.35;
  color: var(--danger);
}

/* Mobile shell: edge-to-edge dialogs with safe-area insets. Desktop unchanged.
   Pulling the backdrop's bottom edge up by the keyboard inset shrinks the
   100%-height panel below with it: header stays put, the body scrolls, and the
   footer actions stay above the keys. --zw-keyboard-inset is set on <html> so it
   reaches here. This panel is teleported to <body>. */
html[data-zw-mobile-shell="1"] .zw-dialog-backdrop {
  padding: 0;
  place-items: stretch;
  bottom: var(--zw-keyboard-inset, 0px);
}

html[data-zw-mobile-shell="1"] .zw-dialog-panel {
  width: 100% !important;
  max-width: none;
  height: 100%;
  max-height: none;
  border-radius: 0;
  border: 0;
  box-shadow: none;
  padding-top: max(0.85rem, env(safe-area-inset-top, 0px));
  padding-bottom: max(0.85rem, env(safe-area-inset-bottom, 0px));
  padding-left: max(0.85rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.85rem, env(safe-area-inset-right, 0px));
}

html[data-zw-mobile-shell="1"] .zw-dialog-panel[data-layout="sheet"] {
  padding: 0;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}

html[data-zw-mobile-shell="1"]
  .zw-dialog-panel[data-layout="sheet"]
  .zw-dialog-header {
  padding-left: max(1.15rem, env(safe-area-inset-left, 0px));
  padding-right: max(1.15rem, env(safe-area-inset-right, 0px));
}

html[data-zw-mobile-shell="1"]
  .zw-dialog-panel[data-layout="sheet"]
  .zw-dialog-footer {
  padding-left: max(1.1rem, env(safe-area-inset-left, 0px));
  padding-right: max(1.1rem, env(safe-area-inset-right, 0px));
  padding-bottom: max(0.9rem, env(safe-area-inset-bottom, 0px));
}

html[data-zw-mobile-shell="1"] .zw-dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  /* Keep a focused field off the bottom edge when the browser scrolls it into
     view after the keyboard shrinks the panel. */
  scroll-padding-bottom: 1.5rem;
}

html[data-zw-mobile-shell="1"] .zw-dialog-panel[data-layout="cozy"] {
  gap: 1rem;
}
</style>
